import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Sparkles, CalendarCheck, Bot } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { productOf } from "@/lib/crm";
import { TYPE_META } from "./TypePicker";

const RX = /<<PRENOTA>>([\s\S]*?)<<FINE>>/;
const clean = (t) => t.replace(RX, "").replace(/<<PRENOTA>>[\s\S]*$/, "").trim();
const extract = (t) => {
  const m = t.match(RX);
  if (!m) return null;
  try { return JSON.parse(m[1]); } catch { return null; }
};

const sessionKey = (slug) => `kredito-chat-${slug}`;
const ackKey = (slug) => `kredito-chat-ack-${slug}`;
const getSession = (slug) => {
  let s = localStorage.getItem(sessionKey(slug));
  if (!s) { s = `chat_${crypto.randomUUID().replace(/-/g, "")}`; localStorage.setItem(sessionKey(slug), s); }
  return s;
};

const Confirm = ({ data, slug, onBooked }) => {
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const book = async () => {
    if (!consent) return toast.error("È necessario il consenso privacy");
    setSaving(true);
    try {
      const num = (v) => (v ? Number(String(v).replace(/[^\d.]/g, "")) || null : null);
      const r = await api.post(`/public/booking/${slug}/book`, {
        ...data, chat_session_id: localStorage.getItem(sessionKey(slug)) || null, email: data.email || "", amount: num(data.amount), duration_months: num(data.duration_months) ? Math.round(num(data.duration_months)) : null,
        income: num(data.income), purpose: data.purpose || "", job: data.job || "", city: data.city || "", summary: data.summary || "", privacy_consent: true,
      });
      onBooked(r.data);
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  return (
    <div data-testid="chat-confirm-card" className="rounded-xl border border-sky-200 bg-sky-50 p-4 space-y-3 text-sm">
      <p className="font-display font-semibold flex items-center gap-2"><CalendarCheck className="h-4 w-4 text-sky-700" /> Riepilogo appuntamento</p>
      <p>{new Date(`${data.date}T${data.time}`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })} alle <b>{data.time}</b> · {TYPE_META[data.type]?.label}</p>
      <p>{data.first_name} {data.last_name} · {data.phone}{data.email ? ` · ${data.email}` : ""}</p>
      <p className="text-slate-600">{productOf(data.product).label}{data.amount ? ` · € ${Number(data.amount).toLocaleString("it-IT")}` : ""}{data.duration_months ? ` · ${data.duration_months} mesi` : ""}{data.purpose ? ` · ${data.purpose}` : ""}</p>
      {(data.summary || data.notes) && <p data-testid="chat-confirm-summary" className="text-slate-600 italic">{data.summary || data.notes}</p>}
      <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
        <Checkbox data-testid="chat-privacy" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
        Acconsento al trattamento dei miei dati personali (GDPR UE 2016/679). <a href={`/prenota/${slug}/privacy`} target="_blank" rel="noreferrer" className="text-sky-700 underline">Informativa</a>
      </label>
      <Button data-testid="chat-confirm-btn" onClick={book} disabled={saving} className="w-full rounded-full bg-[#0F172A] hover:bg-sky-600">{saving ? "Prenotazione…" : "Conferma prenotazione"}</Button>
    </div>
  );
};

export const BookingChat = ({ slug, info, onBooked }) => {
  const [sid] = useState(() => getSession(slug));
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(true);
  const [ack, setAck] = useState(() => localStorage.getItem(ackKey(slug)) === "1");
  const end = useRef(null);
  const accept = () => { localStorage.setItem(ackKey(slug), "1"); setAck(true); };

  useEffect(() => {
    api.get(`/public/booking/${slug}/chat/${sid}`)
      .then((r) => { if (Array.isArray(r.data) && r.data.length) setMsgs(r.data); })
      .catch(() => {})
      .finally(() => setBusy(false));
  }, [slug, sid]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs]);

  const send = async (e) => {
    e?.preventDefault();
    const m = text.trim();
    if (!m || busy || !ack) return;
    setText("");
    setBusy(true);
    setMsgs((x) => [...x, { role: "user", content: m }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/booking/${slug}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sid, message: m, notice_ack: ack }),
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.error) throw new Error(ev.error);
          if (ev.delta) setMsgs((x) => {
            if (!x.length) return x;
            const c = [...x];
            const last = c[c.length - 1] || { role: "assistant", content: "" };
            c[c.length - 1] = { role: "assistant", content: (last.content || "") + ev.delta };
            return c;
          });
        }
      }
    } catch (err) {
      toast.error(err.message || "Errore dell'assistente");
      setMsgs((x) => x.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  const last = [...msgs].reverse().find((m) => m.role === "assistant");
  const proposal = last && !busy ? extract(last.content) : null;

  return (
    <div className="flex flex-col h-[560px] rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="booking-chat">
      <div data-testid="ai-disclosure" className="flex items-start gap-2 bg-violet-50 border-b border-violet-100 px-4 py-2.5 text-xs text-violet-900">
        <Bot className="h-4 w-4 shrink-0 mt-0.5" />
        <span>Stai parlando con un <b>assistente virtuale basato su intelligenza artificiale</b>, non con una persona. Le risposte sono generate automaticamente; i dati che scrivi servono solo a fissare l'appuntamento con {info.agent_name}. Non inserire dati sanitari, IBAN o documenti.</span>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-4 grain">
        <div className="flex gap-3">
          <div className="h-8 w-8 shrink-0 rounded-full bg-sky-600 text-white grid place-items-center"><Sparkles className="h-4 w-4" /></div>
          <div className="rounded-2xl rounded-tl-sm bg-slate-100 px-4 py-2.5 text-sm max-w-[85%]">
            Ciao! Sono l'assistente virtuale (AI) di {info.agent_name}. Ti aiuto a fissare un appuntamento gratuito di {info.duration} minuti. Di cosa hai bisogno? (mutuo, prestito, cessione del quinto, polizza…)
          </div>
        </div>
        {!ack && (
          <div data-testid="chat-privacy-notice" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-slate-700 space-y-3">
            <p className="font-semibold text-slate-900">Prima di iniziare</p>
            <p data-testid="chat-privacy-notice-text">{info.chat_notice}</p>
            <div className="flex flex-wrap items-center gap-3">
              <a data-testid="chat-privacy-link" href={`/prenota/${slug}/privacy`} target="_blank" rel="noreferrer" className="text-sky-700 underline">Leggi l'informativa completa</a>
              <Button data-testid="chat-privacy-ack-btn" size="sm" onClick={accept} className="rounded-full bg-emerald-600 hover:bg-emerald-700">Ho letto, inizia la chat</Button>
            </div>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "gap-3"}`} data-testid={`chat-msg-${i}`}>
            {m.role === "assistant" && <div className="h-8 w-8 shrink-0 rounded-full bg-sky-600 text-white grid place-items-center"><Sparkles className="h-4 w-4" /></div>}
            <div className={`rounded-2xl px-4 py-2.5 text-sm max-w-[85%] whitespace-pre-wrap ${m.role === "user" ? "bg-[#0F172A] text-white rounded-tr-sm" : "bg-slate-100 rounded-tl-sm"}`}>
              {clean(m.content) || <span className="inline-flex gap-1"><span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce" /><span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:120ms]" /><span className="h-1.5 w-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:240ms]" /></span>}
            </div>
          </div>
        ))}
        {proposal && <Confirm data={proposal} slug={slug} onBooked={onBooked} />}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="border-t border-slate-200 p-3 flex gap-2">
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} disabled={!ack} placeholder={ack ? "Scrivi un messaggio…" : "Leggi l'informativa per iniziare"} maxLength={800}
          className="flex-1 rounded-full border border-slate-200 px-4 text-sm outline-none focus:border-sky-500 disabled:bg-slate-50" />
        <button data-testid="chat-send-btn" disabled={busy || !ack || !text.trim()} className="h-10 w-10 rounded-full bg-sky-600 text-white grid place-items-center disabled:opacity-40 hover:bg-sky-700 transition-colors">
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
};
